import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import "./DashboardCard.css";
import "./UpcomingEvents.css"; // TODO migrate remaining styles to tokens
import './primitives/FilterDropdown.css';
import './primitives/Button.css';
import './primitives/Stack.css';
import './primitives/Card.css';
import { useNavigate } from "react-router-dom";
import EventCard from './EventCard'; // ✅ use the compact card
import useIsMobile from '../hooks/useIsMobile';
import AddNewButton from './common/AddNewButton';

function UpcomingEvents({ courseEndeavorId, courseName, onCountUpdate }) {
  const { user } = useAuth();
  const { getFilteredEndeavors, getFilteredEvents, isLoading } = useData();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [events, setEvents] = useState([]);
  const [endeavors, setEndeavors] = useState([]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    date: 'upcoming',
    endeavor: courseEndeavorId ? String(courseEndeavorId) : 'all'
  });

  const userId = user?.id || user?.userId;
  const endeavorsLoading = isLoading(`endeavors_user-${userId}`);

  // === MOVE COUNT EMITTER SETUP TO TOP (before any callbacks that use it) ===
  const onCountUpdateRef = React.useRef(onCountUpdate);
  React.useEffect(()=>{ onCountUpdateRef.current = onCountUpdate; },[onCountUpdate]);

  const lastCountRef = React.useRef(-1);
  const emitCountIfChanged = React.useCallback((count)=>{
    if(onCountUpdateRef.current && lastCountRef.current !== count){
      lastCountRef.current = count;
      onCountUpdateRef.current(count);
    }
  },[]);
  // === END emitter setup ===

  // ✅ PHASE 3: Stabilize DataContext method references
  const getFilteredEndeavorsRef = useRef(getFilteredEndeavors);
  const getFilteredEventsRef = useRef(getFilteredEvents);
  useEffect(() => { 
    getFilteredEndeavorsRef.current = getFilteredEndeavors;
    getFilteredEventsRef.current = getFilteredEvents;
  }, [getFilteredEndeavors, getFilteredEvents]);

  // Load endeavors using DataContext
  const loadEndeavors = useCallback(async () => {
    if (!userId) return [];
    if (courseEndeavorId) {
      const single = [{
        id: Number(courseEndeavorId),
        endeavorId: Number(courseEndeavorId),
        name: courseName || 'Course'
      }];
      setEndeavors(single);
      return single;
    }

    try {
      setError(null);
      console.log('UpcomingEvents: Loading endeavors for user:', userId);
      
      const endeavorsList = await getFilteredEndeavorsRef.current(userId);
      setEndeavors(endeavorsList);
      
      return endeavorsList;
    } catch (err) {
      console.error('UpcomingEvents: Error loading endeavors:', err);
      setError(err);
      return [];
    }
  }, [userId, courseEndeavorId, courseName]); // ✅ PHASE 3: Removed getFilteredEndeavors dependency (uses ref)

  // ✅ Use DataContext filtering to get events from cache
  const loadUserEvents = useCallback(async (endeavorsList) => {
    try {
      setError(null);
      
      // Use DataContext filtering - it handles cache lookup and filtering
      const filterParams = {};
      if (courseEndeavorId) filterParams.endeavorId = courseEndeavorId;

      const list = await getFilteredEventsRef.current(filterParams);
      
      // Enrich with endeavor names
      const nameById = new Map(endeavorsList.map(e => [Number(e.endeavorId||e.id), e.name]));
      const enriched = list.map(ev => {
        const eid = ev.endeavorId || ev.endeavorID || ev.endeavor_id;
        return {
          ...ev,
          endeavorId: eid,
          endeavorName: nameById.get(Number(eid)) || ev.endeavorName || courseName || 'Course'
        };
      });
      
      setEvents(enriched);
      emitCountIfChanged(enriched.length);
    } catch (error) {
      console.error('UpcomingEvents: Error loading filtered events:', error);
      setError(error);
    }
  }, [courseEndeavorId, courseName, emitCountIfChanged]);

  // Load data when component mounts or user changes
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    loadEndeavors().then(list => { if(!cancelled) return loadUserEvents(list); });
    return () => { cancelled = true; };
  }, [userId, loadEndeavors, loadUserEvents]);

  const handleRefresh = async () => {
    const endeavorsList = await loadEndeavors();
    await loadUserEvents(endeavorsList);
  };

  // Filter and sort events based on current UI filters (date/endeavor dropdowns)
  const getDisplayedEvents = () => {
    let filteredEvents = [...events];

    // Filter by endeavor
    if (filters.endeavor !== 'all') {
      filteredEvents = filteredEvents.filter(event => 
        (event.endeavorId) === parseInt(filters.endeavor)
      );
    }

    // Filter by date
    const now = new Date();
    if (filters.date === 'upcoming') {
      filteredEvents = filteredEvents.filter(event => {
        const eventDateStr = event.startDatetime || event.eventDate;
        if (!eventDateStr) return false;
        
        let eventDate;
        if (eventDateStr.includes('T')) {
          eventDate = new Date(eventDateStr);
        } else {
          eventDate = new Date(eventDateStr + 'T00:00:00');
        }
        
        return !isNaN(eventDate.getTime()) && eventDate >= now;
      });
    } else if (filters.date === 'past') {
      filteredEvents = filteredEvents.filter(event => {
        const eventDateStr = event.startDatetime || event.eventDate;
        if (!eventDateStr) return false;
        
        let eventDate;
        if (eventDateStr.includes('T')) {
          eventDate = new Date(eventDateStr);
        } else {
          eventDate = new Date(eventDateStr + 'T00:00:00');
        }
        
        return !isNaN(eventDate.getTime()) && eventDate < now;
      });
    }

    // Sort by date
    filteredEvents.sort((a, b) => {
      const getEventDate = (event) => {
        const dateStr = event.startDatetime || event.eventDate;
        if (!dateStr) return new Date(0);
        
        if (dateStr.includes('T')) {
          return new Date(dateStr);
        } else {
          return new Date(dateStr + 'T00:00:00');
        }
      };
      
      const dateA = getEventDate(a);
      const dateB = getEventDate(b);
      
      if (filters.date === 'past') {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });

    return filteredEvents;
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return 'No date';
    
    let eventDate;
    
    if (dateString.includes('T')) {
      eventDate = new Date(dateString);
    } else {
      eventDate = new Date(dateString + 'T00:00:00');
    }
    
    if (isNaN(eventDate.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    
    const isToday = eventDate.toDateString() === now.toDateString();
    const isTomorrow = eventDate.toDateString() === tomorrow.toDateString();
    
    if (isToday) {
      return `${eventDate.toLocaleDateString('en-US', { 
        weekday: 'long' 
      })} at ${eventDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else if (isTomorrow) {
      return `${eventDate.toLocaleDateString('en-US', { 
        weekday: 'long' 
      })} at ${eventDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    } else {
      return eventDate.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  // ✅ NEW: Calculate upcoming and past events
  const getEventCounts = () => {
    const now = new Date();
    
    const upcomingCount = events.filter(event => {
      const eventDateStr = event.startDatetime || event.eventDate;
      if (!eventDateStr) return false;
      
      let eventDate;
      if (eventDateStr.includes('T')) {
        eventDate = new Date(eventDateStr);
      } else {
        eventDate = new Date(eventDateStr + 'T00:00:00');
      }
      
      return !isNaN(eventDate.getTime()) && eventDate >= now;
    }).length;
    
    const pastCount = events.filter(event => {
      const eventDateStr = event.startDatetime || event.eventDate;
      if (!eventDateStr) return false;
      
      let eventDate;
      if (eventDateStr.includes('T')) {
        eventDate = new Date(eventDateStr);
      } else {
        eventDate = new Date(eventDateStr + 'T00:00:00');
      }
      
      return !isNaN(eventDate.getTime()) && eventDate < now;
    }).length;
    
    return { upcomingCount, pastCount };
  };

  const filteredEvents = getDisplayedEvents();
  const { upcomingCount, pastCount } = getEventCounts();
  const loading = endeavorsLoading;

  const handleCreateEvent = () => {
    const path = isMobile ? '/m/create-event' : '/create-event';
    navigate(path, {
      state: {
        selectedDate: new Date(),
        returnTo: '/dashboard'
      }
    });
  };

  // ADD: dynamic title
  const eventsTitle = courseEndeavorId ? 'Course Events' : 'Events';

  // Fire effect when events change post-load:
  React.useEffect(()=>{
    if(!endeavorsLoading){
      emitCountIfChanged(events.length);
    }
  },[events, endeavorsLoading, emitCountIfChanged]);

  return (
    <div className={`dashboard-card upcoming-events-card tokenizing ${isMobile ? 'full-height' : ''}`}>
      <div className="card-main-header">
        <div className="card-title-with-counts">
          <h2 className="card-main-title">
            <span className="header-icon-wrapper events-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </span>
            <span className="events-header-text">{eventsTitle}</span>
          </h2>
          <div className="card-counts">
            {loading ? (
              <span className="count-text">Loading...</span>
            ) : (
              <span className="count-text">
                <span className="count-number">{pastCount}</span> past, <span className="count-number">{upcomingCount}</span> upcoming
              </span>
            )}
          </div>
        </div>
        <div className="card-header-actions">
          <AddNewButton onClick={handleCreateEvent} disabled={loading} ariaLabel="Create new event" />
        </div>
      </div>

      <div className="card-filters-segmented">
        <div className="segmented-control" role="radiogroup" aria-label="Timeline filter">
          {[
            { value: 'upcoming', label: 'Upcoming' },
            { value: 'all', label: 'All' },
            { value: 'past', label: 'Past' }
          ].map(opt => (
            <button
              key={opt.value}
              className={`segment-btn ${filters.date === opt.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('date', opt.value)}
              role="radio"
              aria-checked={filters.date === opt.value}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {!courseEndeavorId && (
          <select
            id="course-filter"
            className="filter-dropdown compact"
            aria-label="Course"
            value={filters.endeavor}
            onChange={(e) => handleFilterChange('endeavor', e.target.value)}
          >
            <option value="all">All Courses</option>
            {endeavors.map((endeavor) => (
              <option
                key={endeavor.endeavorId || endeavor.id}
                value={(endeavor.endeavorId || endeavor.id).toString()}
              >
                {endeavor.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Progress Summary */}
      {!loading && !error && events.length > 0 && (
        <div className="progress-summary-bar">
          <div className="progress-track">
            <div
              className="progress-fill events-progress"
              style={{ width: `${Math.round((pastCount / events.length) * 100)}%` }}
            />
          </div>
          <span className="progress-text">
            {Math.round((pastCount / events.length) * 100)}% of events completed
          </span>
        </div>
      )}

      <div className="card-content events-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading events...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>Failed to load events: {error.message || error}</p>
            <button type="button" className="btn btn-secondary" onClick={handleRefresh}>🔄 Try Again</button>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="empty-state celebration-empty events-celebration">
            <div className="mascot-container">
              <div className="mascot">📅</div>
              <div className="mascot-speech">
                <div className="speech-bubble">
                  No events yet!
                </div>
              </div>
            </div>
            <div className="celebration-content">
              <h3>Add Events to Start Planning Your Schedule!</h3>
              <p>
                {filters.date === 'upcoming'
                  ? 'Create your first event to see it appear here.'
                  : filters.date === 'past'
                    ? 'No past events recorded yet.'
                    : 'No events match your filters.'}
              </p>
              <div className="celebration-actions">
                <AddNewButton onClick={handleCreateEvent} ariaLabel="Create event" />
              </div>
            </div>
          </div>
        ) : (
          <div className="events-scroller">
            <div className="events-list">
              {filteredEvents.map((event) => {
                const eventId = event.eventId || event.id;
                return (
                  <EventCard
                    key={eventId}
                    event={event}
                    formatDateTime={formatEventDate}
                    onClick={() =>
                      navigate(
                        isMobile ? `/m/event/${eventId}` : `/event/${eventId}`,
                        { state: { returnTo: '/dashboard' } }
                      )
                    }
                  />
                );
              })}
            </div>
            {filteredEvents.length > 3 && (
              <div className="list-footer">
                {filteredEvents.length} total events
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UpcomingEvents;